import type { PackageHashOptions } from '@ms-cloudpack/common-types';
import filenamify from 'filenamify';
import path from 'path';
import type { PartialContext } from '../types/Context.js';

/**
 * Get the output path (with hash) in the cache for a package bundle.
 */
export async function getBundleLocation(
  options: Omit<PackageHashOptions, 'isSourceHashingEnabled'> & {
    /** Use this hash instead of calculating. */
    hash?: string;
  },
  context: PartialContext<'packages' | 'packageHashes', 'cachePath'>,
): Promise<{ path: string; folderName: string }> {
  const { hash, ...hashOptions } = options;
  const { packages, packageHashes, session } = context;
  const { cachePath } = session;
  const definition = await packages.get(options.packagePath);

  const { name, version } = definition;

  const hashResult =
    hash ||
    (await packageHashes.get({
      ...hashOptions,
      // We don't use source hashing for location,
      // because we do not want to create a new folder for each change in a package.
      isSourceHashingEnabled: false,
    }));
  // Derive where the output should be.
  const folderName = `${filenamify(name, { replacement: '^' })}-${version}-${hashResult}`;
  const outputPath = path.join(cachePath, folderName);

  return {
    path: outputPath,
    folderName,
  };
}
